import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { AdminInvite } from '../models/AdminInvite'
import { adminInviteSchema } from '../models/schema'
import {
  createAdminInvite as createAdminInviteAPI,
  deleteAdminInvite as deleteAdminInviteAPI,
  fetchAdminInviteById,
  fetchAdminInvites,
  updateAdminInvite as updateAdminInviteAPI,
} from '../services/AdminInviteService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<AdminInvite> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'adminInvite',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getAdminInvitesSuccess,
  setQuery: setAdminInviteQuery,
  reset: resetAdminInviteState,
  resetQueryAndIds: resetAdminInviteQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: AdminInvite }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectAdminInvites = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedAdminInvite = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectAdminInviteById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectAdminInvitesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedAdminInvite =
  (adminInvite: AdminInvite | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<AdminInvite>(adminInvite))
    )

export const getAdminInvites = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchAdminInvites(params),
    [adminInviteSchema],
    slice.actions
  )

export const createAdminInvite = (adminInvite: AdminInvite) =>
  SBAPI.CreateDispatched(
    createAdminInviteAPI(adminInvite),
    adminInviteSchema,
    slice.actions
  )

export const getAdminInviteWithId = (id: number) =>
  SBAPI.FetchDispatched<AdminInvite>(
    fetchAdminInviteById(id),
    adminInviteSchema,
    slice.actions
  )

export const updateAdminInvite = (adminInvite: AdminInvite) =>
  SBAPI.UpdateDispatched<AdminInvite>(
    updateAdminInviteAPI(adminInvite),
    adminInvite,
    slice.actions
  )

export const deleteAdminInvite = (adminInvite: AdminInvite) =>
  SBAPI.DestroyDispatched<AdminInvite>(
    deleteAdminInviteAPI(adminInvite),
    adminInvite,
    slice.actions
  )
